/** This provider overwrites the I18nProvider of LinguiJS by adding logic to
 * detect the local language of the user. As we don't have an option to switch
 * languages, we detect the locale in getLocale and use that.
 */
import * as React from 'react';
import { I18nProvider } from '@lingui/react';
import { i18n } from '@lingui/core';
import { userSession } from 'state/session';

export const I18nContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { locale, brand } = userSession().useState();
  const [i18nLoaded, setI18nLoaded] = React.useState(false);

  React.useEffect(() => {
    if (locale) {
      (async () => {
        let messages;
        try {
          /** Import the branded locale first to be able to load the regular locale in the catch block if it fails */
          const { messages: brandedMessages } = await import(`./locales/${locale}-${brand}.mjs`);
          const commonMessages = await getTranslationCatalogByLocale();
          messages = { ...brandedMessages, ...commonMessages };
        } catch {
          messages = await getTranslationCatalogByLocale();
        }
        i18n.loadAndActivate({ locale, messages });
        setI18nLoaded(true);
      })();
    }
    /** Locale and brand should be enough to trigger useEffect */
    // eslint-disable-next-line
  }, [locale, brand]);

  const getTranslationCatalogByLocale = async () => {
    try {
      const { messages } = await import(`./locales/${locale}.mjs`);
      return messages;
    } catch (e) {
      console.error(e);
      throw new Error('Your locale failed to load. Please make sure you are online and reload the page.');
    }
  };

  if (!locale || !i18nLoaded) {
    return null;
  }
  return <I18nProvider i18n={i18n}>{children}</I18nProvider>;
};
